import { formatCurrency } from '@/utils/format'
import { Head, router } from '@inertiajs/react'
import {
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconCash, IconChecks, IconInfoCircle, IconMessage2, IconShoppingCart } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ChangeEvent, ComponentType } from 'react'

type StatIcon = ComponentType<{ size?: number | string }>

type UserRow = {
  id: number
  name: string
  email: string
  orders_created: number
  money_collected: number
  sms_sent: number
}

type OrganizationRow = {
  id: number
  name: string
  slug: string
  kind_store: boolean
  orders_created: number
  revenue_collected: number
  sms_sent: number
}

type Summary = {
  orders_created: number
  paid_orders: number
  revenue_collected: number
  sms_sent: number
}

const ALL_ORGANIZATIONS = 'all'

const PERIOD_PRESETS = [
  { key: 'last_week', label: 'Semaine dernière' },
  { key: 'this_month', label: 'Ce mois' },
  { key: 'last_month', label: 'Mois dernier' },
] as const

// Explanatory copy for the revenue column, verified against app/services/admin_dashboard_report.rb.
// Revenue is summed from paid orders by payment date and credited to whoever cashed the payment.
const ENCAISSE_HINTS = {
  organization:
    "Somme des prix des prestations encaissées par l'organisation sur les commandes payées, additionnée selon la date de paiement.",
  user:
    "Montant des prestations encaissé par la personne qui a pris le paiement, selon la date de paiement. Ce n'est pas forcément la personne qui a créé la commande.",
} as const

function InfoHint({ label }: { label: string }) {
  return (
    <Tooltip label={label} multiline w={260} withArrow events={{ hover: true, focus: true, touch: true }}>
      <ThemeIcon variant="transparent" color="gray" size="sm" radius="xl" aria-label={label} tabIndex={0}>
        <IconInfoCircle size={15} />
      </ThemeIcon>
    </Tooltip>
  )
}

function ColumnHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <Group gap={4} align="center" wrap="nowrap">
      <span>{label}</span>
      <InfoHint label={hint} />
    </Group>
  )
}

function presetRange(key: string): { start: string; end: string } {
  if (key === 'last_week') {
    const reference = dayjs().subtract(1, 'week')
    return { start: reference.startOf('week').format('YYYY-MM-DD'), end: reference.endOf('week').format('YYYY-MM-DD') }
  }
  if (key === 'last_month') {
    const reference = dayjs().subtract(1, 'month')
    return { start: reference.startOf('month').format('YYYY-MM-DD'), end: reference.endOf('month').format('YYYY-MM-DD') }
  }
  const reference = dayjs()
  return { start: reference.startOf('month').format('YYYY-MM-DD'), end: reference.endOf('month').format('YYYY-MM-DD') }
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string; icon: StatIcon; color: string }) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" align="flex-start">
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
        <ThemeIcon variant="light" color={color} size="lg" radius="md">
          <Icon size={18} />
        </ThemeIcon>
      </Group>
      <Text fz={28} fw={700} mt="sm">
        {value}
      </Text>
    </Card>
  )
}

export default function AdminDashboardShow({
  selected_organization_slug,
  start_date,
  end_date,
  summary,
  users,
  organizations,
}: {
  selected_organization_slug: string | null
  start_date: string | null
  end_date: string | null
  summary: Summary
  users: UserRow[]
  organizations: OrganizationRow[]
}) {
  const selectedOrganization = organizations.find((organization) => organization.slug === selected_organization_slug)
  const isAllOrganizations = !selectedOrganization
  const isAllTime = !start_date && !end_date

  const buildQuery = (overrides: Record<string, string | null>) => {
    const next: Record<string, string | null> = {
      organization: selected_organization_slug,
      start_date,
      end_date,
      ...overrides,
    }
    return Object.fromEntries(Object.entries(next).filter(([, value]) => Boolean(value)))
  }

  const navigate = (overrides: Record<string, string | null>) =>
    router.get('/admin_dashboard', buildQuery(overrides), { preserveState: true, preserveScroll: true })

  const handleOrganizationChange = (value: string | null) =>
    navigate({ organization: value && value !== ALL_ORGANIZATIONS ? value : null })

  const handleStartDateChange = (event: ChangeEvent<HTMLInputElement>) =>
    navigate({ start_date: event.target.value || null })

  const handleEndDateChange = (event: ChangeEvent<HTMLInputElement>) => navigate({ end_date: event.target.value || null })

  const applyPreset = (key: string) => {
    const range = presetRange(key)
    navigate({ start_date: range.start, end_date: range.end })
  }

  const clearPeriod = () => navigate({ start_date: null, end_date: null })

  const isPresetActive = (key: string) => {
    const range = presetRange(key)
    return start_date === range.start && end_date === range.end
  }

  const organizationOptions = [
    { value: ALL_ORGANIZATIONS, label: 'Toutes les organisations' },
    ...organizations.map((organization) => ({ value: organization.slug, label: organization.name })),
  ]

  const formattedStart = start_date ? dayjs(start_date).format('DD/MM/YYYY') : '…'
  const formattedEnd = end_date ? dayjs(end_date).format('DD/MM/YYYY') : '…'
  const periodLabel = isAllTime ? 'Toutes les périodes' : `${formattedStart} – ${formattedEnd}`
  const scopeLabel = selectedOrganization ? selectedOrganization.name : 'Toutes les organisations'

  const stats: { label: string; value: string; icon: StatIcon; color: string }[] = [
    { label: 'Commandes créées', value: String(summary.orders_created), icon: IconShoppingCart, color: 'blue' },
    { label: 'Commandes payées', value: String(summary.paid_orders), icon: IconChecks, color: 'grape' },
    { label: 'Montant encaissé', value: formatCurrency(summary.revenue_collected), icon: IconCash, color: 'teal' },
    { label: 'SMS envoyés', value: String(summary.sms_sent), icon: IconMessage2, color: 'orange' },
  ]

  return (
    <>
      <Head title="Tableau de bord" />

      <Stack gap="xl">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <div>
            <Title>Tableau de bord</Title>
            <Text c="dimmed">
              {scopeLabel} · {periodLabel}
            </Text>
          </div>
          <Select
            label="Organisation"
            data={organizationOptions}
            value={selected_organization_slug ?? ALL_ORGANIZATIONS}
            onChange={handleOrganizationChange}
            allowDeselect={false}
            w={260}
          />
        </Group>

        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <Group gap="xs" align="flex-end">
            <Button.Group>
              {PERIOD_PRESETS.map((preset) => (
                <Button
                  key={preset.key}
                  variant={isPresetActive(preset.key) ? 'filled' : 'default'}
                  onClick={() => applyPreset(preset.key)}
                >
                  {preset.label}
                </Button>
              ))}
            </Button.Group>
            <Button variant={isAllTime ? 'filled' : 'default'} onClick={clearPeriod}>
              Tout
            </Button>
          </Group>
          <Group gap="sm" align="flex-end">
            <TextInput type="date" label="Du" value={start_date ?? ''} onChange={handleStartDateChange} w={160} />
            <TextInput type="date" label="Au" value={end_date ?? ''} onChange={handleEndDateChange} w={160} />
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
          {stats.map((stat) => (
            <StatCard key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} color={stat.color} />
          ))}
        </SimpleGrid>

        {isAllOrganizations && <OrganizationsTable organizations={organizations} onSelect={handleOrganizationChange} />}

        <UsersTable users={users} />
      </Stack>
    </>
  )
}

function OrganizationsTable({
  organizations,
  onSelect,
}: {
  organizations: OrganizationRow[]
  onSelect: (slug: string) => void
}) {
  return (
    <Stack gap="sm">
      <Title order={3}>Par organisation</Title>
      <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
        <Table.ScrollContainer minWidth={600}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Organisation</Table.Th>
                <Table.Th w={110}>Type</Table.Th>
                <Table.Th w={150}>Commandes</Table.Th>
                <Table.Th w={170}>
                  <ColumnHeader label="Encaissé" hint={ENCAISSE_HINTS.organization} />
                </Table.Th>
                <Table.Th w={120}>SMS</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {organizations.map((organization) => (
                <Table.Tr
                  key={organization.id}
                  onClick={() => onSelect(organization.slug)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td fw={500} c="brand">
                    {organization.name}
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={organization.kind_store ? 'teal' : 'blue'}>
                      {organization.kind_store ? 'Boutique' : 'Atelier'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{organization.orders_created}</Table.Td>
                  <Table.Td fw={500}>{formatCurrency(organization.revenue_collected)}</Table.Td>
                  <Table.Td>{organization.sms_sent}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  )
}

function UsersTable({ users }: { users: UserRow[] }) {
  return (
    <Stack gap="sm">
      <Title order={3}>Par utilisateur</Title>
      <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
        <Table.ScrollContainer minWidth={600}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Utilisateur</Table.Th>
                <Table.Th w={160}>Commandes créées</Table.Th>
                <Table.Th w={190}>
                  <ColumnHeader label="Montant encaissé" hint={ENCAISSE_HINTS.user} />
                </Table.Th>
                <Table.Th w={140}>SMS envoyés</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      Aucune activité enregistrée
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {users.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text fw={500}>{row.name}</Text>
                    <Text size="xs" c="dimmed">
                      {row.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>{row.orders_created}</Table.Td>
                  <Table.Td fw={500}>{formatCurrency(row.money_collected)}</Table.Td>
                  <Table.Td>{row.sms_sent}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  )
}
